/**
 * FormField Component
 * Combines Label + Input with error/hint display
 * WCAG 2.1 AA compliant
 */

import React from 'react';
import { Label } from '@/components/atoms/Label/Label';
import { Input, InputProps, Text } from '@/components/atoms';

export interface FormFieldProps extends Omit<InputProps, 'error' | 'errorMessage' | 'helperText' | 'wrapperClassName'> {
  /**
   * Field label text
   */
  label: string;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Hint text to display below the input
   */
  hint?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
}

export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  ({ label, error, hint, required, id, name, ...inputProps }, ref) => {
    // Generate a unique ID for accessibility
    const fieldId = id || name || `field-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${fieldId}-error`;
    const hintId = `${fieldId}-hint`;

    return (
      <div className="w-full">
        <Label htmlFor={fieldId} required={required}>
          {label}
        </Label>
        <div className="mt-1">
          <Input
            ref={ref}
            id={fieldId}
            name={name}
            required={required}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? errorId : hint ? hintId : undefined
            }
            {...inputProps}
          />
        </div>
        {error && (
          <div className="mt-1">
            <Text
              id={errorId}
              variant="caption"
              color="error"
              as="span"
              role="alert"
              aria-live="polite"
            >
              {error}
            </Text>
          </div>
        )}
        {hint && !error && (
          <div className="mt-1">
            <Text id={hintId} variant="caption" as="span">
              {hint}
            </Text>
          </div>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
